import { SocketClient } from "cbcore-ts"
import {
    IF,
    IS_LIKE_NULL,
    IS_NOT_LIKE_NULL,
    nil,
    NO,
    UIRoute,
    UITextField,
    UITextView,
    UIView,
    UIViewController,
    YES
} from "uicore-ts"
import { CBDataViewCellDescriptor } from "./Custom components/CBDataView"
import { CBDialogViewShower } from "./Custom components/CBDialogViewShower"
import { CellView } from "./Custom components/CellView"
import { RHButton } from "./Custom components/RH/RHButton"
import { RHColor } from "./Custom components/RH/RHColor"
import { RHDataView } from "./Custom components/RH/RHDataView"
import { RHLabeledSearchableDropdown } from "./Custom components/RH/RHLabeledSearchableDropdown"
import { RHTextField } from "./Custom components/RH/RHTextField"
import { LanguageService } from "./LanguageService"
import { currencyFormatter, numberFormatter, percentFormatter } from "./RHRStatisticsViewController"


// Define a common interface for data items
interface StatisticsComparisonDataItem {
    entityName: string; // Holds procurerName or offererName
    parameterValue: number;
    contractCount: number;
    averagePerContract?: number;
}


const range = (start: number, end: number) => Array.from({ length: (end - start) }, (v, k) => k + start)
const filterOperators = ["<", "<=", "==", "!=", ">=", ">"]


export class RHRStatisticsComparisonViewController extends UIViewController {
    
    
    entityType: "procurer" | "offerer" | string = "procurer"
    
    get titleKey(): string {
        
        const result = "" + this.class.name + "Title_" + (this.entityType ?? "procurer")
        return result
        
    }
    
    explanationLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        // Text will be set dynamically
    })
    
    yearDropdown = new RHLabeledSearchableDropdown<string>().configuredWithObject({
        label: { text: "Vali aasta" },
        dropdown: {
            data: range(2023, new Date().getFullYear() + 1).map(year => {
                    return {
                        
                        _id: year + "",
                        title: { en: year + "", est: year + "" },
                        isADropdownDataSection: NO,
                        isADropdownDataRow: YES,
                        
                        attachedObject: year + "",
                        
                        itemCode: year + "",
                        dropdownCode: "Years"
                        
                    }
                }
            ),
            dialogContainerView: this.view,
            isSingleSelection: YES,
            
            controlEventTargetAccumulator: {
                SelectionDidChange: () => {
                    // Only update the route, handleRoute will trigger content update if needed
                    if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                        UIRoute.currentRoute.routeBySettingParameterInComponent(
                            RHRStatisticsComparisonViewController.routeComponentName,
                            RHRStatisticsComparisonViewController.ParameterIdentifierName.year,
                            this.yearDropdown.selectedItemCodes.firstElement
                        ).apply()
                    }
                }
            }
        }
    }).addedAsSubviewToView(this.view)
    
    procurementProcedureCodeDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            label: { text: "Menetlusliik" },
            dropdown: {
                data: [], // Data loaded dynamically
                dialogContainerView: this.view,
                isSingleSelection: NO, // Allow multiple selections
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (this.routeComponent) {
                            const route = UIRoute.currentRoute
                                .routeBySettingParameterInComponent(
                                    RHRStatisticsComparisonViewController.routeComponentName,
                                    RHRStatisticsComparisonViewController.ParameterIdentifierName.procType, // Use new
                                                                                                            // param
                                                                                                            // name
                                    this.procurementProcedureCodeDropdown.selectedItemCodes.join("+") ?? "" // Join
                                                                                                            // selected
                                                                                                            // codes
                                )
                            
                            // Decide whether to replace or push history based on if the param already exists
                            if (UIRoute.currentRoute.componentWithViewController(
                                this.class)?.parameters[RHRStatisticsComparisonViewController.ParameterIdentifierName.procType]) {
                                route.apply()
                            }
                            else {
                                route.applyByReplacingCurrentRouteInHistory()
                            }
                        }
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)
    
    comparisonParameterDropdown = new RHLabeledSearchableDropdown<{
        name: string
        type: string
    }>().configuredWithObject({
        label: { text: "Vali võrdlusparameeter" },
        dropdown: {
            dialogContainerView: this.view,
            isSingleSelection: YES,
            
            controlEventTargetAccumulator: {
                SelectionDidChange: () => {
                    // Only update the route, handleRoute will trigger content update if needed
                    if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                        UIRoute.currentRoute.routeBySettingParameterInComponent(
                            RHRStatisticsComparisonViewController.routeComponentName,
                            RHRStatisticsComparisonViewController.ParameterIdentifierName.parameter,
                            this.comparisonParameterDropdown.dropdown.selectedItemCodes.firstElement ?? nil
                        ).apply()
                    }
                }
            }
        }
    }).addedAsSubviewToView(this.view)
    
    
    filterFromLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        text: "Filtreeri alates:",
        textAlignment: UITextView.textAlignment.right
    })
    
    filterFromValueField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        
        viewHTMLElement: {
            placeholder: "Sisesta väärtus filtreerimiseks",
            type: "number"  // Use number input type for better mobile experience
        },
        text: "1",
        type: "number",
        controlEventTargetAccumulator: {
            TextChange: () => {
                if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                    
                    this._ignoreNextRouteChange = YES
                    
                    UIRoute.currentRoute.routeBySettingParameterInComponent(
                        RHRStatisticsComparisonViewController.routeComponentName,
                        RHRStatisticsComparisonViewController.ParameterIdentifierName.filterFromValue,
                        this.filterFromValueField.text
                    ).applyByReplacingCurrentRouteInHistory()
                    
                    this.applyFilterToDataView(
                        IF(this.filterFromValueField.text.length)(
                            () => this.filterFromValueField.text.numericalValue
                        )(),
                        IF(this.filterUpToValueField.text.length)(
                            () => this.filterUpToValueField.text.numericalValue
                        )()
                    )
                    
                }
            }
        }
        
        
    })
    
    filterUpToLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        text: " kuni:",
        textAlignment: UITextView.textAlignment.right
    })
    
    filterUpToValueField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        
        viewHTMLElement: {
            placeholder: "Sisesta väärtus filtreerimiseks",
            type: "number"  // Use number input type for better mobile experience
        },
        text: "1",
        type: "number",
        controlEventTargetAccumulator: {
            TextChange: () => {
                if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                    
                    this._ignoreNextRouteChange = YES
                    
                    UIRoute.currentRoute.routeBySettingParameterInComponent(
                        RHRStatisticsComparisonViewController.routeComponentName,
                        RHRStatisticsComparisonViewController.ParameterIdentifierName.filterUpToValue,
                        this.filterUpToValueField.text
                    ).applyByReplacingCurrentRouteInHistory()
                    
                    this.applyFilterToDataView(
                        IF(this.filterFromValueField.text.length)(
                            () => this.filterFromValueField.text.numericalValue
                        )(),
                        IF(this.filterUpToValueField.text.length)(
                            () => this.filterUpToValueField.text.numericalValue
                        )()
                    )
                    
                }
            }
        }
        
        
    })
    
    clearFilterButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Eemalda filter"
        },
        // type: "",
        controlEventTargetAccumulator: {
            PointerUpInside: (sender, event) => {
                this.clearFilter()
            }
        }
    })
    
    statisticsView = new RHDataView<StatisticsComparisonDataItem>().configuredWithObject({
        tableView: { allRowsHaveEqualHeight: NO },
        searchTextField: {
            placeholderText: LanguageService.stringForCurrentLanguage({
                    en: "Search by keywords (comma separated)",
                    est: "Otsi märksõnade järgi (komadega eraldatud)"
                }
            )
        }
    }).addedAsSubviewToView(this.view)
    bottomView = new UIView().addedAsSubviewToView(this.view)
    
    // Filter properties
    
    
    allData: StatisticsComparisonDataItem[] = []
    _isInitialized = NO
    _ignoreNextRouteChange = NO
    _ignoreHandleRoute = NO
    
    get isInitialized(): boolean {
        return this._isInitialized
    }
    
    set isInitialized(isInitialized: boolean) {
        
        this._isInitialized = isInitialized
        // this.view.alpha = IF(isInitialized)(RETURNER(1)).ELSE(RETURNER(0.5))
        
        if (!isInitialized) {
            
            CBDialogViewShower.showActionIndicatorDialog("Server is initializing")
            this.statisticsView.data = []
            this.statisticsView.descriptors = []
            
        }
        else {
            CBDialogViewShower.hideActionIndicatorDialog()
        }
        
    }
    
    
    constructor(view: UIView) {
        
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor
        })
        
        this.isInitialized = NO
        
        // Initial content update will be triggered by the first handleRoute call
        // via the hashchange event after the app loads.
        // If immediate update is needed before hashchange, call handleRoute explicitly:
        // this.handleRoute(UIRoute.currentRoute); // Or call updateContent directly if route isn't ready
        
    }
    
    private updateUITextsBasedOnEntityType() {
        if (this.entityType === "procurer") {
            this.explanationLabel.text = "Siin on võimalik võrrelda erinevate hankijate statistikat valitud aastal."
        }
        else {
            this.explanationLabel.text = "Siin on võimalik võrrelda erinevate pakkujate statistikat valitud aastal."
        }
        this.view.setNeedsLayout() // Ensure layout updates if text changes size
    }
    
    
    get selectedComparisonParameterName(): string {
        
        // Return the first selected parameter or default to contractMonetarySum
        return UIRoute.currentRoute.componentWithViewController(
            this.class)?.parameters.parameter ?? "contractMonetarySum"
        
    }
    
    
    private clearFilter() {
        
        // Clear the filter value field
        this.filterFromValueField.text = ""
        this.filterUpToValueField.text = ""
        
        // Reset the operator to default
        // this.filterOperatorDropdown.selectedItemCodes = [">="]
        
        this.filterFromValueField.sendControlEventForKey(UITextField.controlEvent.TextChange)
        
    }
    
    
    private applyFilterToDataView(filterFromValue: number, filterUpToValue: number) {
        
        if (IS_LIKE_NULL(filterFromValue) && IS_LIKE_NULL(filterUpToValue)) {
            // No valid filter value, show all data
            this.statisticsView.data = this.allData
            return
        }
        
        // Apply filter based on the selected operator
        const filteredData = this.allData.filter(item => {
            
            const value = item.parameterValue
            
            if (IS_NOT_LIKE_NULL(filterFromValue) && filterFromValue > value) {
                return NO
            }
            
            if (IS_NOT_LIKE_NULL(filterUpToValue) && filterUpToValue < value) {
                return NO
            }
            
            return YES
            
        })
        
        this.statisticsView.data = filteredData
    }
    
    
    private async retrieveParameterNames() {
        
        try {
            
            const response = await SocketClient.RetrieveStatisticsParameterNames({
                entityType: this.entityType as any
            })
            
            const comparisonParameters = response.result
            
            this.comparisonParameterDropdown.dropdown.data = comparisonParameters.map(parameterObject => {
                return {
                    _id: parameterObject.name,
                    title: LanguageService.localizedTextObjectForKey(
                        parameterObject.name,
                        parameterObject.name
                    ),
                    isADropdownDataSection: NO,
                    isADropdownDataRow: YES,
                    
                    attachedObject: parameterObject,
                    
                    itemCode: parameterObject.name,
                    dropdownCode: "Parameters"
                }
            })
            
            this.comparisonParameterDropdown.dropdown.selectedItemCodes = [this.selectedComparisonParameterName]
            
        }
        catch (error) {
            
            console.error("Error fetching parameter names:", error)
            
        }
        
    }
    
    private async retrieveProcurementTypesAndInitDropdown() {
        
        try {
            
            const procedureCodes = (await SocketClient.RetrieveProcurementProcedureCodes()).result
            const data = Object.entries(procedureCodes).map(([code, name]) => ({
                _id: code,
                title: LanguageService.localizedTextObjectForKey(
                    code + "_procurement_type",
                    name
                ),
                isADropdownDataSection: NO,
                isADropdownDataRow: YES,
                attachedObject: code,
                itemCode: code,
                dropdownCode: "ProcurementTypes"
            }))
            this.procurementProcedureCodeDropdown.dropdown.data = data
            
            // Select all the codes by default
            this.procurementProcedureCodeDropdown.selectedItemCodes = procedureCodes.allKeys
            
            
            // const procurementTypes =
            // UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]
            // UIRoute.currentRoute.routeBySettingParameterInComponent( RHRStatisticsViewController.routeComponentName,
            // RHRStatisticsViewController.ParameterIdentifierName.procType, procurementTypes ||
            // procedureCodes.join("+") ).applyByReplacingCurrentRouteInHistory()
            
            this.procurementProcedureCodeDropdown.enabled = YES
            
        }
        catch (error) {
            console.error("Error fetching procurement types:", error)
        }
        
    }
    
    
    // Renamed updateStatisticsView to fetchAndDisplayStatisticsData
    private async fetchAndDisplayStatisticsData(
        entityType: string,
        selectedYear: string,
        comparisonParameterName: string,
        procurementProcedureCodes: string[]
    ) {
        
        if (!this.yearDropdown.dropdown.selectedItemCodes.length) {
            this.statisticsView.data = []
            return
        }
        
        this.statisticsView.isSearchAvailable = YES
        
        if (!this.statisticsView.data.length) {
            
            this.statisticsView.enabled = NO
            
        }
        
        // console.trace("About to call the big expensive function")
        // console.log(UIRoute.currentRoute.stringRepresentation)
        // Use the consolidated endpoint
        const response = await SocketClient.StatisticsComparison({
            entityType: entityType as any,
            year: selectedYear,
            parameterName: comparisonParameterName,
            procurementProcedureCodes: procurementProcedureCodes
        })
        
        // Get name and type for the selected comparison parameter
        const selectedComparisonParameterObject = this.comparisonParameterDropdown.dropdown.data.find(
            p => p.attachedObject.name === this.selectedComparisonParameterName)?.attachedObject ?? {
            name: this.selectedComparisonParameterName,
            type: "count"
        }
        
        // Configure column descriptors dynamically
        let descriptors: CBDataViewCellDescriptor<StatisticsComparisonDataItem>[] = [
            {
                keyPath: "entityName", // Use the common keyPath
                title: entityType === "procurer" ? "Hankija" : "Pakkuja", // Dynamic title
                allowSorting: NO, // Usually names are not sorted by default, but could be YES
                updateCell: (
                    cellView: CellView,
                    dataItem: StatisticsComparisonDataItem
                ) => cellView.titleLabel.text = dataItem.entityName
            },
            {
                keyPath: "parameterValue",
                title: LanguageService.stringForKey(
                    selectedComparisonParameterObject.name,
                    LanguageService.currentLanguageKey,
                    selectedComparisonParameterObject.name
                ),
                allowSorting: YES,
                dataType: "number",
                updateCell: (cellView: CellView, dataItem: any) => {
                    let value = dataItem.parameterValue
                    
                    // Format based on parameter type from server metadata
                    if (selectedComparisonParameterObject.type === "monetary") {
                        cellView.titleLabel.text = currencyFormatter.format(value)
                    }
                    else if (selectedComparisonParameterObject.type === "percentage") {
                        cellView.titleLabel.text = percentFormatter.format(value)
                    }
                    else if (selectedComparisonParameterObject.type === "average") {
                        cellView.titleLabel.text = numberFormatter.format(value)
                    }
                    else {
                        // Handle other non-numeric string values if necessary
                        cellView.titleLabel.text = value
                    }
                }
            }
        ]
        
        // Add contract count column if the selected parameter isn't the contract count itself
        const contractCountParamName: string = IF(entityType === "procurer")(() =>
            "numberOfContractsForTheYear"
        ).ELSE(() =>
            "numberOfContractsWonForTheYear"
        )
        
        if (selectedComparisonParameterObject.name !== contractCountParamName) {
            descriptors.push({
                keyPath: "contractCount",
                title: IF(entityType === "procurer")(() => "Lepingute arv").ELSE(() => "Võidetud lepingute arv"),
                allowSorting: YES,
                dataType: "number",
                updateCell: (
                    cellView: CellView,
                    dataItem: StatisticsComparisonDataItem
                ) => cellView.titleLabel.text = dataItem.contractCount.toString()
            })
        }
        
        
        // Add average per contract for monetary parameters
        if (selectedComparisonParameterObject.type === "monetary") {
            descriptors.push({
                keyPath: "averagePerContract",
                title: "Keskmine lepingu maksumus (€)",
                allowSorting: YES,
                dataType: "number",
                updateCell: (cellView: CellView, dataItem: any) => {
                    if (isFinite(dataItem.averagePerContract)) {
                        cellView.titleLabel.text = currencyFormatter.format(dataItem.averagePerContract)
                    }
                    else {
                        cellView.titleLabel.text = "N/A"
                    }
                }
            })
        }
        
        this.statisticsView.descriptors = descriptors
        this.statisticsView.hasResizableColumns = YES
        
        // Map backend data to the common StatisticsComparisonDataItem interface
        this.allData = response.result.data.map((item: any) => {
            
            return {
                entityName: item.entityName,
                parameterValue: item.parameterValue,
                contractCount: item.contractCount, // Assumes backend provides this consistently
                averagePerContract: item.averagePerContract
            }
            
        })
        
        // Apply filter after data is fetched
        this.applyFilterToDataView(
            IF(this.filterFromValueField.text.length)(
                () => this.filterFromValueField.text.numericalValue
            )(),
            IF(this.filterUpToValueField.text.length)(
                () => this.filterUpToValueField.text.numericalValue
            )()
        )
        
        this.statisticsView.enabled = YES
        
        
    }
    
    
    static override readonly routeComponentName = "comparestatistics"
    
    static override readonly ParameterIdentifierName = {
        "entityType": "entityType",
        "year": "year",
        "parameter": "parameter",
        "procType": "procType",
        "filterFromValue": "filterFromValue",
        "filterUpToValue": "filterUpToValue"
    }
    
    override async viewWillAppear() {
        
        await super.viewWillAppear()
        
        this.isInitialized = this.isInitialized
        
        this.updateUITextsBasedOnEntityType()
        await this.retrieveParameterNames()
        await this.retrieveProcurementTypesAndInitDropdown()
        this.comparisonParameterDropdown.selectedItemCodes = [this.selectedComparisonParameterName]
        
        console.log("Statistics comparison view controller finished initializing")
        
        this.isInitialized = YES
        
        
    }
    
    override async viewDidAppear() {
        
    }
    
    override async viewWillDisappear() {
        
        this.isInitialized = NO
        CBDialogViewShower.hideActionIndicatorDialog()
        
    }
    
    override async handleRoute(route: UIRoute) {
        
        super.handleRoute(route)
        
        if (!this.routeComponent || this._ignoreHandleRoute) {
            return
        }
        
        const entityTypeFromRoute = this.routeComponent?.parameters?.entityType
        if (entityTypeFromRoute && entityTypeFromRoute != this.entityType && this.isInitialized) {
            
            this._ignoreHandleRoute = YES
            this.entityType = entityTypeFromRoute
            this.updateUITextsBasedOnEntityType()
            await this.retrieveParameterNames()
            await this.retrieveProcurementTypesAndInitDropdown()
            this.isInitialized = YES
            
            this._ignoreHandleRoute = NO
            
        }
        
        const currentYear = decodeURIComponent(this.routeComponent?.parameters?.year || new Date().getFullYear()
            .toString())
        const currentComparisonParameterName = decodeURIComponent(
            this.routeComponent?.parameters?.parameter || "contractMonetarySum")
        const currentProcurementProcedureCodes = decodeURIComponent(this.routeComponent?.parameters?.procType || "")
            .split("+")
        
        const currentFilterFromValue = decodeURIComponent(this.routeComponent?.parameters?.filterFromValue || "")
        const currentFilterUpToValue = decodeURIComponent(this.routeComponent?.parameters?.filterUpToValue || "")
        
        this._ignoreHandleRoute = YES
        
        // --- Update UI elements directly related to route parameters ---
        this.yearDropdown.dropdown.selectedItemCodes = [currentYear]
        this.comparisonParameterDropdown.selectedItemCodes = [currentComparisonParameterName]
        this.procurementProcedureCodeDropdown.selectedItemCodes = currentProcurementProcedureCodes
        this.filterFromValueField.text = currentFilterFromValue
        this.filterUpToValueField.text = currentFilterUpToValue
        
        this._ignoreHandleRoute = NO
        
        
        if (this.isInitialized && !this._ignoreNextRouteChange) {
            
            if (!currentProcurementProcedureCodes.join("+") || !currentComparisonParameterName || !currentYear) {
                
                this.statisticsView.data = []
                
            }
            else {
                
                CBDialogViewShower.showActionIndicatorDialog("Loading data")
                await this.fetchAndDisplayStatisticsData(
                    this.entityType,
                    currentYear,
                    currentComparisonParameterName,
                    currentProcurementProcedureCodes
                )
                this.applyFilterToDataView(
                    IF(this.filterFromValueField.text.length)(
                        () => this.filterFromValueField.text.numericalValue
                    )(),
                    IF(this.filterUpToValueField.text.length)(
                        () => this.filterUpToValueField.text.numericalValue
                    )()
                )
                CBDialogViewShower.hideActionIndicatorDialog()
                
                this.view.setNeedsLayoutUpToRootView()
                
            }
            
        }
        else {
            
            this._ignoreNextRouteChange = NO
            
        }
        
        
    }
    
    override updateViewConstraints() {
        
        super.updateViewConstraints()
        
    }
    
    override updateViewStyles() {
        
        super.updateViewStyles()
        
    }
    
    override viewDidLayoutSubviews() {
        
        super.viewDidLayoutSubviews()
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        
        // View bounds
        const bounds = this.view.bounds.rectangleWithInset(padding)
        
        // Position explanationLabel at the top
        this.explanationLabel.frame = bounds.rectangleWithHeight(
            [this.explanationLabel.intrinsicContentHeight(bounds.width), labelHeight].max()
        )
        
        // Position dropdowns side by side
        this.explanationLabel.frame.rectangleForNextRow(
            padding,
            [
                this.yearDropdown.intrinsicContentHeight((bounds.width / 3) - padding * 2),
                padding
            ].max()
        ).distributeViewsEquallyAlongWidth(
            [this.yearDropdown, this.procurementProcedureCodeDropdown],
            padding
        )
        
        // Filter label
        this.comparisonParameterDropdown.frame = this.yearDropdown.frame.rectangleWithWidth(bounds.width)
            .rectangleForNextRow(
                padding * 1,
                padding * 2
            )
            .rectangleWithWidth(bounds.width * 0.5 - padding)
        
        this.filterFromLabel.frame = this.comparisonParameterDropdown.frame
            .rectangleWithX(this.procurementProcedureCodeDropdown.frame.x)
            .rectangleByAddingY(padding * 2)
            .rectangleWithWidth(
                this.filterFromLabel.intrinsicContentWidth(
                    this.comparisonParameterDropdown.frame.height
                ) + padding * 0.5
            )
        this.filterFromValueField.frame = this.filterFromLabel.frame.rectangleForNextColumn(padding)
            .rectangleWithWidth(270)
        
        this.filterUpToLabel.frame = this.filterFromValueField.frame.rectangleForNextColumn(
            padding,
            this.filterUpToLabel.intrinsicContentWidth(this.filterFromLabel.frame.height) + padding
        )
        this.filterUpToValueField.frame = this.filterUpToLabel.frame.rectangleForNextColumn(padding)
            .rectangleWithWidth(270)
        
        
        // Clear filter button
        this.clearFilterButton.frame = this.filterUpToValueField.frame.rectangleForNextColumn(padding)
            .rectangleWithWidth(130)
            .rectangleByAddingY(-padding * 0.33)
        
        // Statistics view
        const statisticsViewHeight = Math.min(
            this.statisticsView.intrinsicContentHeight(bounds.width), // Calculate natural height needed
            1200 // Set a maximum height (e.g., 1000px)
        )
        this.statisticsView.frame = this.comparisonParameterDropdown.frame.rectangleWithWidth(bounds.width)
            .rectangleForNextRow(
                padding * 3,
                Math.max(statisticsViewHeight, padding) // Ensure minimum height is padding
            )
        this.bottomView.frame = this.statisticsView.frame.rectangleForNextRow(padding, 10)
    }
    
}
